<template>
  <div class="list pending-interview-page">
    <div class="breadcrumb">
      <Breadcrumb>
        <Breadcrumb-item>外教招聘</Breadcrumb-item>
        <Breadcrumb-item>待面试老师</Breadcrumb-item>
      </Breadcrumb>
    </div>
    <div class="page-head">
      <div>
        <h2 class="page-title">待面试老师</h2>
        <p class="page-sub">管理面试和试讲课安排，评估外教教学能力 · Manage interview &amp; demo</p>
      </div>
      <div class="page-actions">
        <Button :type="view === 'list' ? 'primary' : 'ghost'" icon="android-list" @click="switchView('list')">列表视图</Button>
        <span class="pill is-blue">今日面试 ({{ summary.todayInterview || 0 }})</span>
        <span class="pill is-green">今日试讲 ({{ summary.todayDemo || 0 }})</span>
        <Button :type="view === 'schedule' ? 'primary' : 'ghost'" icon="calendar" @click="switchView('schedule')">面试时间表</Button>
      </div>
    </div>

    <template v-if="view === 'list'">
      <div class="search" @keyup.enter="search">
        <Form :label-width="80" :show-message="false" label-position="right">
          <Row type="flex" :gutter="16">
            <Col :span="6">
              <Form-item label="ID/姓名">
                <Input v-model="queryParam.keyword" placeholder="老师ID / 姓名 / 邮箱"></Input>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="国籍">
                <Input v-model="queryParam.nationality" placeholder="国籍"></Input>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="渠道">
                <Select v-model="queryParam.regFrom" placeholder="全部" clearable>
                  <Option v-for="item in channelOptions" :value="item.param" :key="item.id">{{ item.status === 0 ? (item.name + '（停用）') : item.name }}</Option>
                </Select>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="推荐人">
                <div class="referrer-field">
                  <Input v-model="referrerName" :disabled="noReferrer" placeholder="推荐人姓名"></Input>
                  <Checkbox v-model="noReferrer">无推荐人</Checkbox>
                </div>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="HR">
                <Select v-model="queryParam.hr" placeholder="全部" clearable transfer @on-change="search">
                  <Option v-for="item in $constant.hrList" :value="item.code" :key="item.code">{{ item.text }}</Option>
                </Select>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="面试状态">
                <Select v-model="queryParam.interviewStatus" placeholder="全部" clearable>
                  <Option v-for="item in stageStatus" :value="item.code" :key="item.code">{{ item.text }}</Option>
                </Select>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="试讲状态">
                <Select v-model="queryParam.demoStatus" placeholder="全部" clearable>
                  <Option v-for="item in stageStatus" :value="item.code" :key="item.code">{{ item.text }}</Option>
                </Select>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="复审">
                <Select v-model="queryParam.reviewStatus" placeholder="全部" clearable>
                  <Option v-for="item in reviewStatus" :value="item.code" :key="item.code">{{ item.text }}</Option>
                </Select>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="Offer状态">
                <Select v-model="queryParam.offerStatus" placeholder="全部" clearable>
                  <Option v-for="item in offerStatus" :value="item.code" :key="item.code">{{ item.text }}</Option>
                </Select>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="面试时间">
                <DatePicker :value="interviewRange" type="daterange" format="yyyy-MM-dd" clearable
                  placeholder="选择日期区间" style="width: 100%" transfer
                  @on-change="onInterviewRangeChange"></DatePicker>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item label="试讲时间">
                <DatePicker :value="demoRange" type="daterange" format="yyyy-MM-dd" clearable
                  placeholder="选择日期区间" style="width: 100%" transfer
                  @on-change="onDemoRangeChange"></DatePicker>
              </Form-item>
            </Col>
            <Col :span="6">
              <Form-item :label-width="20">
                <Button type="primary" icon="search" :loading="loading" @click="search">搜索</Button>
                <Button style="margin-left: 8px" @click="reset">重置</Button>
              </Form-item>
            </Col>
          </Row>
        </Form>
      </div>
      <div class="batch-bar">
        <span class="batch-hint">已选 {{ selectedRows.length }} 人（当前页）</span>
        <Button type="primary" :disabled="!selectedRows.length || batchLoading" :loading="batchLoading === 'review'" @click="batchSubmitReview">批量提交复审</Button>
        <Button :disabled="!selectedRows.length || batchLoading" :loading="batchLoading === 'reserve'" @click="openBatchReserve">批量转储备</Button>
        <Button type="success" :disabled="!selectedRows.length || batchLoading" :loading="batchLoading === 'offer'" @click="batchOfferSent">批量发Offer</Button>
      </div>
      <Table border :columns="columns" :data="tableData" :loading="loading" size="small" @on-selection-change="onSelectionChange"></Table>
      <Page class="page" :total="totalCount" :current="pageIndex" :page-size="pageSize" show-total show-sizer
        show-elevator :page-size-opts="[10, 20, 50, 100]" placement="top"
        @on-change="onPageChange" @on-page-size-change="onPageSizeChange"></Page>
    </template>

    <recruit-schedule-board v-else ref="board" @open-tch="row => openTchInfoModal(row.tchId)"
      @on-change="afterChange(false)"></recruit-schedule-board>

    <recruit-schedule-modal v-model="scheduleVisible" :row="activeRow" :kind="scheduleKind" @on-success="afterChange(true)"></recruit-schedule-modal>
    <recruit-notes-modal v-model="notesVisible" :row="activeRow" @on-success="afterChange(true)"></recruit-notes-modal>
    <recruit-review-modal v-model="reviewVisible" :row="activeRow" :mode="reviewMode" @on-success="afterChange(true)"></recruit-review-modal>
    <recruit-reason-modal v-model="reasonVisible" :title="reason.title" :label="reason.label" :desc="reason.desc"
      :placeholder="reason.placeholder" :init-reason="reason.init" :loading="reasonLoading"
      :kind-options="reason.kindOptions" :kind-label="reason.kindLabel" :init-kind="reason.initKind"
      @on-ok="onReasonOk"></recruit-reason-modal>
    <tch-info-modal :value="showTchInfoModal" @on-close="showTchInfoModal = false" :tchId="tchId" :showTchSelf="true"
      :successCallback="reloadPage" recruit-flow-entry="待面试老师"></tch-info-modal>
    <tch-apply-form-modal :visible="showApplyModal" @on-close="showApplyModal=false" :tchId="applyTchId"></tch-apply-form-modal>
  </div>
</template>
<script>
import TchInfoModal from '../modal/TchInfoModal.vue'
import { fetchTchScoreMap, applyTchScoresToRows, renderTchNameWithScore } from '@/utils/tchScore'
import RecruitScheduleBoard from './RecruitScheduleBoard.vue'
import RecruitScheduleModal from './modal/RecruitScheduleModal.vue'
import RecruitNotesModal from './modal/RecruitNotesModal.vue'
import RecruitReviewModal from './modal/RecruitReviewModal.vue'
import RecruitReasonModal from './modal/RecruitReasonModal.vue'
import TchApplyFormModal from '../modal/TchApplyFormModal.vue'
import { STAGE_STATUS, REVIEW_STATUS, OFFER_STATUS, NO_REFERRER, findStatus, tchName, shortTime } from './recruitConst'

/** daterange [yyyy-MM-dd, yyyy-MM-dd] → 半开区间 {from,to}：起日 00:00:00，止日次日 00:00:00 */
function rangeToHalfOpen (range) {
  if (!range || !range[0] || !range[1]) {
    return null
  }
  let start = String(range[0]).trim()
  let end = String(range[1]).trim()
  if (!start || !end) {
    return null
  }
  let endDate = new Date(end.replace(/-/g, '/') + ' 00:00:00')
  if (isNaN(endDate.getTime())) {
    return null
  }
  endDate.setDate(endDate.getDate() + 1)
  let y = endDate.getFullYear()
  let m = ('0' + (endDate.getMonth() + 1)).slice(-2)
  let d = ('0' + endDate.getDate()).slice(-2)
  return {
    from: start + ' 00:00:00',
    to: y + '-' + m + '-' + d + ' 00:00:00'
  }
}

function emptyQuery () {
  return {
    keyword: '',
    nationality: '',
    regFrom: '',
    hr: '',
    interviewStatus: '',
    demoStatus: '',
    reviewStatus: '',
    offerStatus: ''
  }
}

export default {
  name: 'TchPendingInterview',
  components: { TchInfoModal, RecruitScheduleBoard, RecruitScheduleModal, RecruitNotesModal, RecruitReviewModal, RecruitReasonModal, TchApplyFormModal },
  data () {
    return {
      view: 'list',
      stageStatus: STAGE_STATUS,
      reviewStatus: REVIEW_STATUS,
      offerStatus: OFFER_STATUS,
      channelOptions: [],
      queryParam: emptyQuery(),
      interviewRange: [],
      demoRange: [],
      referrerName: '',
      noReferrer: false,
      appliedQuery: {},
      loading: false,
      rankScoreSeq: 0,
      tableData: [],
      totalCount: 0,
      pageIndex: 1,
      pageSize: 20,
      summary: {},
      activeRow: null,
      scheduleVisible: false,
      scheduleKind: 'both',
      notesVisible: false,
      reviewVisible: false,
      reviewMode: 'review',
      reasonVisible: false,
      reasonLoading: false,
      reason: {type: '', title: '', label: '', desc: '', placeholder: '', init: '', row: null, kind: '', kindOptions: null, kindLabel: '类型', initKind: ''},
      showTchInfoModal: false,
      tchId: null,
      showApplyModal: false,
      applyTchId: null,
      selectedRows: [],
      batchLoading: false,
      columns: []
    }
  },
  created () {
    this.columns = this.buildColumns()
    this.loadRegChannels()
    this.loadSummary()
    this.search()
  },
  methods: {
    buildColumns () {
      return [
        {type: 'selection', width: 48, align: 'center'},
        {title: '老师ID', key: 'tchId', width: 80, align: 'center'},
        {
          title: '姓名',
          width: 140,
          align: 'center',
          render: (h, params) => {
            let row = params.row
            return renderTchNameWithScore(h, {
              text: tchName(row) || String(row.tchId),
              score: row.monthRankScore,
              onClick: () => this.openTchInfoModal(row.tchId)
            })
          }
        },
        {title: '国籍', key: 'nationality', width: 100, align: 'center'},
        {title: '渠道', key: 'regFrom', width: 100, align: 'center'},
        {
          title: '推荐人',
          width: 100,
          align: 'center',
          render: (h, params) => h('span', params.row.inviteTchName || '—')
        },
        {
          title: 'HR',
          width: 150,
          align: 'center',
          render: (h, params) => {
            let row = params.row
            let options = this.$constant.hrList.slice()
            if (row.hr && !options.some(item => item.code === row.hr)) {
              options = [{code: row.hr, text: row.hr}].concat(options)
            }
            return h('Select', {
              props: {
                value: row.hr,
                transfer: true,
                clearable: true
              },
              on: {
                'on-change': (value) => {
                  const newValue = value || ''
                  if (newValue !== (row.hr || '')) {
                    row.hr = newValue
                    this.updateTchDirect({tchForm: {tchId: row.tchId, hr: newValue}})
                  }
                }
              }
            }, options.map(item => {
              return h('Option', {props: {value: item.code}, key: item.code}, item.text)
            }))
          }
        },
        {
          title: '面试状态',
          width: 140,
          align: 'center',
          render: (h, params) => this.renderStage(h, params.row, 'interview')
        },
        {
          title: '试讲状态',
          width: 140,
          align: 'center',
          render: (h, params) => this.renderStage(h, params.row, 'demo')
        },
        {
          title: '邀约环节',
          width: 240,
          align: 'center',
          render: (h, params) => {
            let row = params.row
            return h('div', {class: 'invite-cell'}, [
              h('Button', {
                props: {size: 'small', icon: 'calendar'},
                on: {click: () => this.openSchedule(row, 'both')}
              }, '安排面试/试讲'),
              h('Button', {
                props: {size: 'small', type: row.hasNotes ? 'success' : 'primary', icon: 'document-text'},
                style: {marginLeft: '4px'},
                on: {click: () => this.openNotes(row)}
              }, row.hasNotes ? '查看笔记' : '添加笔记')
            ])
          }
        },
        {
          title: '复审',
          width: 150,
          align: 'center',
          render: (h, params) => this.renderReview(h, params.row)
        },
        {
          title: '发Offer',
          width: 120,
          align: 'center',
          render: (h, params) => this.renderOffer(h, params.row)
        },
        {
          title: '操作',
          width: 200,
          align: 'center',
          fixed: 'right',
          render: (h, params) => {
            let row = params.row
            let children = [
              h('a', {on: {click: () => this.openReserve(row)}}, '转储备')
            ]
            if (row.applyStep !== null && row.applyStep !== undefined && row.applyStep !== '') {
              children.push(h('span', {style: {margin: '0 6px', color: '#dddee1'}}, '|'))
              children.push(h('a', {on: {click: () => this.onShowApply(row)}}, '注册资料'))
            }
            return h('div', children)
          }
        }
      ]
    },
    renderPill (h, item, clickable) {
      let cls = {'status-pill': true, 'is-clickable': clickable}
      cls['tone-' + item.color] = true
      return h('span', {class: cls}, item.text + (clickable ? ' ▾' : ''))
    },
    renderStage (h, row, kind) {
      let status = kind === 'interview' ? row.interviewStatus : row.demoStatus
      let time = kind === 'interview' ? row.interviewTime : row.demoTime
      let failReason = kind === 'interview' ? row.interviewFailReason : row.demoFailReason
      let item = findStatus(STAGE_STATUS, status, 'pending')
      let menu = STAGE_STATUS.map(function (s) {
        return h('Dropdown-item', {props: {name: s.code, selected: s.code === item.code}}, s.text)
      })
      let children = [
        h('Dropdown', {
          props: {trigger: 'click', transfer: true},
          on: {'on-click': (name) => this.changeStage(row, kind, name)}
        }, [
          this.renderPill(h, item, true),
          h('Dropdown-menu', {slot: 'list'}, menu)
        ])
      ]
      if (time && item.code !== 'pending') {
        children.push(h('div', {class: 'cell-time'}, shortTime(time)))
      }
      if (item.code === 'failed' && failReason) {
        children.push(h('Tooltip', {props: {content: failReason, transfer: true, placement: 'top'}}, [
          h('div', {class: 'cell-reason'}, '理由：' + failReason)
        ]))
      }
      return h('div', children)
    },
    renderReview (h, row) {
      let status = row.reviewStatus || 'submit'
      if (status === 'submit') {
        let ready = !!row.notesComplete
        let btn = h('Button', {
          props: {size: 'small', type: 'primary', disabled: !ready},
          on: {click: () => this.submitReview(row)}
        }, '提交复审')
        if (ready) {
          return btn
        }
        return h('Tooltip', {props: {content: '请先填写完整面试笔记与试讲打分', transfer: true, placement: 'top'}}, [btn])
      }
      let item = findStatus(REVIEW_STATUS, status, 'submit')
      if (status === 'pending_review') {
        return h('div', [
          this.renderPill(h, item, false),
          h('Button', {
            props: {size: 'small', type: 'warning'},
            style: {marginLeft: '4px'},
            on: {click: () => this.openReview(row, 'review')}
          }, '复审')
        ])
      }
      return h('a', {
        attrs: {title: '查看复审结果'},
        on: {click: () => this.openReview(row, 'view')}
      }, [
        this.renderPill(h, item, false),
        row.reviewGrade ? h('span', {class: 'cell-grade'}, row.reviewGrade) : null
      ])
    },
    renderOffer (h, row) {
      let item = findStatus(OFFER_STATUS, row.offerStatus, 'pending')
      let canEdit = row.reviewStatus === 'approved'
      let children = []
      if (!canEdit) {
        children.push(h('Tooltip', {props: {content: '复审通过后才可发Offer', transfer: true, placement: 'top'}}, [
          h('span', {class: 'status-pill tone-gray is-disabled'}, item.text)
        ]))
      } else {
        let menu = OFFER_STATUS.map(function (s) {
          return h('Dropdown-item', {props: {name: s.code, selected: s.code === item.code}}, s.text)
        })
        children.push(h('Dropdown', {
          props: {trigger: 'click', transfer: true},
          on: {'on-click': (name) => this.changeOffer(row, name)}
        }, [
          this.renderPill(h, item, true),
          h('Dropdown-menu', {slot: 'list'}, menu)
        ]))
      }
      if (item.code === 'rejected' && row.offerRejectReason) {
        children.push(h('Tooltip', {props: {content: row.offerRejectReason, transfer: true, placement: 'top'}}, [
          h('div', {class: 'cell-reason'}, '理由：' + row.offerRejectReason)
        ]))
      }
      return h('div', children)
    },
    loadRegChannels () {
      this.$http.get('/tch/reg-channel/options').then((res) => {
        if (res && res.status === 'ok' && res.data) {
          this.channelOptions = res.data
        }
      }).catch(() => {})
    },
    loadSummary () {
      this.$http.post('/tchRecruit/interview/summary', {}, {silent: true}).then((res) => {
        if (res && res.status === 'ok' && res.data) {
          this.summary = res.data
        }
      }).catch(() => {})
    },
    switchView (view) {
      this.view = view
      if (view === 'list') {
        this.loadList()
      }
    },
    onInterviewRangeChange (value) {
      this.interviewRange = (value && value[0] && value[1]) ? [value[0], value[1]] : []
    },
    onDemoRangeChange (value) {
      this.demoRange = (value && value[0] && value[1]) ? [value[0], value[1]] : []
    },
    buildQuery () {
      let q = {}
      let src = this.queryParam
      Object.keys(src).forEach(function (k) {
        let v = src[k]
        if (v !== null && v !== undefined && String(v).trim() !== '') {
          q[k] = String(v).trim()
        }
      })
      if (this.noReferrer) {
        q.referrer = NO_REFERRER
      } else if (this.referrerName && this.referrerName.trim()) {
        q.referrer = this.referrerName.trim()
      }
      let interviewHalf = rangeToHalfOpen(this.interviewRange)
      if (interviewHalf) {
        q.interviewFrom = interviewHalf.from
        q.interviewTo = interviewHalf.to
      }
      let demoHalf = rangeToHalfOpen(this.demoRange)
      if (demoHalf) {
        q.demoFrom = demoHalf.from
        q.demoTo = demoHalf.to
      }
      return q
    },
    search () {
      this.appliedQuery = this.buildQuery()
      this.pageIndex = 1
      this.loadList()
    },
    reset () {
      this.queryParam = emptyQuery()
      this.interviewRange = []
      this.demoRange = []
      this.referrerName = ''
      this.noReferrer = false
      this.search()
    },
    reloadPage () {
      this.loadList()
    },
    loadList () {
      let params = Object.assign({}, this.appliedQuery, {
        pageNum: this.pageIndex,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize
      })
      this.loading = true
      this.$http.post('/tchRecruit/interview/list', params, {silent: true}).then((res) => {
        if (res && res.status === 'ok') {
          let data = res.data || {}
          this.tableData = data.data || []
          this.loadMonthRankScores()
          this.totalCount = data.totalCount || 0
          this.selectedRows = []
        } else {
          this.tableData = []
          this.totalCount = 0
          this.selectedRows = []
          this.$Message.error((res && res.msg) || '列表加载失败')
        }
      }).catch(() => {}).finally(() => {
        this.loading = false
      })
    },
    onPageChange (page) {
      this.pageIndex = page
      this.loadList()
    },
    onPageSizeChange (size) {
      this.pageSize = size
      this.pageIndex = 1
      this.loadList()
    },
    afterChange (reloadList) {
      if (reloadList && this.view === 'list') {
        this.loadList()
      }
      this.loadSummary()
      this.$root.$emit('recruit-badges-refresh')
    },
    loadMonthRankScores () {
      const rows = this.tableData || []
      if (!rows.length) {
        return
      }
      const ids = rows.map(function (row) { return row.tchId }).filter(function (id) { return id != null })
      const seq = ++this.rankScoreSeq
      const self = this
      fetchTchScoreMap(this.$http, ids).then(function (map) {
        if (seq !== self.rankScoreSeq) {
          return
        }
        applyTchScoresToRows(self, rows, 'tchId', 'monthRankScore', map)
      })
    },
    onShowApply (row) {
      this.applyTchId = row.tchId
      this.showApplyModal = true
    },
    openTchInfoModal (tchId) {
      this.tchId = Number(tchId)
      this.showTchInfoModal = true
    },
    openSchedule (row, kind) {
      this.activeRow = row
      this.scheduleKind = kind
      this.scheduleVisible = true
    },
    openNotes (row) {
      this.activeRow = row
      this.notesVisible = true
    },
    openReview (row, mode) {
      this.activeRow = row
      this.reviewMode = mode
      this.reviewVisible = true
    },
    openReason (opts) {
      this.reason = Object.assign({
        type: '', title: '', label: '', desc: '', placeholder: '', init: '', row: null, kind: '',
        kindOptions: null, kindLabel: '类型', initKind: ''
      }, opts)
      this.reasonLoading = false
      this.reasonVisible = true
    },
    updateTchDirect (req) {
      this.$http.post('/tch/update-direct', req).then(res => {
        if (res.status === 'ok') {
          this.$Message.success('更新成功')
        } else {
          this.$Message.error(res.msg)
        }
      }).catch(() => {
        this.$Message.error('更新失败')
      })
    },
    action (url, params, successText) {
      return this.$http.post(url, params, {silent: true}).then((res) => {
        if (res && res.status === 'ok') {
          this.$Message.success(successText)
          this.afterChange(true)
          return true
        }
        this.$Message.error((res && res.msg) || '操作失败')
        return false
      }).catch(() => false)
    },
    changeStage (row, kind, status) {
      let current = (kind === 'interview' ? row.interviewStatus : row.demoStatus) || 'pending'
      let label = kind === 'interview' ? '面试' : '试讲'
      if (status === 'failed') {
        let name = tchName(row)
        this.openReason({
          type: 'stageFail',
          kind: kind,
          row: row,
          title: '填写未通过理由' + (name ? ' - ' + name : ''),
          label: '未通过理由',
          desc: '确认后将标记' + label + '未通过，老师转到「已审核老师」并发送拒绝邮件。',
          placeholder: '请填写' + label + '未通过的原因',
          init: (kind === 'interview' ? row.interviewFailReason : row.demoFailReason) || ''
        })
        return
      }
      if (status === current) {
        return
      }
      let time = kind === 'interview' ? row.interviewTime : row.demoTime
      if ((status === 'scheduled' || status === 'confirmed') && !time) {
        this.openSchedule(row, kind)
        return
      }
      let text = findStatus(STAGE_STATUS, status).text
      this.action('/tchRecruit/interview/status', {tchId: row.tchId, kind: kind, status: status},
        label + '状态已更新为「' + text + '」')
    },
    submitReview (row) {
      if (!row.notesComplete) {
        this.$Message.error('请先完善面试笔记与试讲打分后再提交复审')
        return
      }
      this.action('/tchRecruit/interview/submit-review', {tchId: row.tchId}, tchName(row) + ' 已提交复审')
    },
    changeOffer (row, status) {
      if (row.reviewStatus !== 'approved') {
        this.$Message.error('复审通过后才可发Offer')
        return
      }
      if (status === (row.offerStatus || 'pending') && status !== 'rejected') {
        return
      }
      let name = tchName(row)
      if (status === 'rejected') {
        this.openReason({
          type: 'offerReject',
          row: row,
          title: '填写拒绝理由' + (name ? ' - ' + name : ''),
          label: '拒绝理由',
          placeholder: '请填写拒绝 Offer 的原因',
          init: row.offerRejectReason || ''
        })
        return
      }
      let text = findStatus(OFFER_STATUS, status).text
      let doIt = () => this.action('/tchRecruit/interview/offer', {tchId: row.tchId, offerStatus: status},
        '发Offer状态已更新为「' + text + '」')
      if (status === 'accepted') {
        this.$Modal.confirm({
          title: '接受Offer',
          content: '接受后老师将进入培训中，确定 ' + name + ' 接受Offer？',
          onOk: doIt
        })
        return
      }
      doIt()
    },
    openReserve (row) {
      let name = tchName(row)
      this.openReason({
        type: 'reserve',
        row: row,
        title: '转储备' + (name ? ' - ' + name : ''),
        label: '储备原因',
        desc: '转储备后老师从待面试移出，可在「储备老师」重新激活。',
        placeholder: '请填写转储备的原因'
      })
    },

    onSelectionChange (rows) {
      this.selectedRows = rows || []
    },
    selectedTchIds () {
      return this.selectedRows.map(function (r) { return r.tchId }).filter(Boolean).join(',')
    },
    formatBatchMsg (data) {
      let success = (data && data.successCount) || 0
      let skip = (data && data.skipCount) || 0
      let fail = (data && data.failCount) || 0
      let parts = ['成功 ' + success + ' 人']
      if (skip) {
        parts.push('跳过 ' + skip + ' 人')
      }
      if (fail) {
        parts.push('失败 ' + fail + ' 人')
      }
      let reasons = []
      let items = (data && data.items) || []
      items.forEach(function (it) {
        if (it.outcome !== 'success' && it.reason) {
          reasons.push('ID' + it.tchId + '：' + it.reason)
        }
      })
      let msg = parts.join('，')
      if (reasons.length) {
        msg += '。' + reasons.slice(0, 5).join('；')
        if (reasons.length > 5) {
          msg += '…'
        }
      }
      return {msg: msg, success: success, skip: skip, fail: fail}
    },
    runBatch (url, params, loadingKey) {
      if (!params.tchIds) {
        this.$Message.warning('请先勾选老师')
        return Promise.resolve(false)
      }
      this.batchLoading = loadingKey || true
      return this.$http.post(url, params, {silent: true}).then((res) => {
        if (res && res.status === 'ok') {
          let info = this.formatBatchMsg(res.data || {})
          if (info.fail > 0) {
            this.$Message.warning(info.msg)
          } else if (info.skip > 0 && info.success === 0) {
            this.$Message.warning(info.msg)
          } else if (info.skip > 0) {
            this.$Message.warning(info.msg)
          } else {
            this.$Message.success(info.msg)
          }
          this.afterChange(true)
          return true
        }
        this.$Message.error((res && res.msg) || '批量操作失败')
        return false
      }).catch(() => {
        this.$Message.error('批量操作失败')
        return false
      }).finally(() => {
        this.batchLoading = false
      })
    },
    batchSubmitReview () {
      this.runBatch('/tchRecruit/interview/batch-submit-review', {tchIds: this.selectedTchIds()}, 'review')
    },
    openBatchReserve () {
      let n = this.selectedRows.length
      if (!n) {
        this.$Message.warning('请先勾选老师')
        return
      }
      this.openReason({
        type: 'batchReserve',
        row: {tchId: 0},
        title: '批量转储备（' + n + ' 人）',
        label: '储备原因',
        desc: '所选老师将共用该原因转储备；不符合条件的会跳过。',
        placeholder: '请填写转储备的原因'
      })
    },
    batchOfferSent () {
      let n = this.selectedRows.length
      if (!n) {
        this.$Message.warning('请先勾选老师')
        return
      }
      this.$Modal.confirm({
        title: '批量发Offer',
        content: '将把所选 ' + n + ' 人标为「已发 Offer」（仅复审通过且仍在面试中的会成功，其余跳过）。确定？',
        onOk: () => this.runBatch('/tchRecruit/interview/batch-offer', {tchIds: this.selectedTchIds()}, 'offer')
      })
    },
    onReasonOk (text, kind) {
      let r = this.reason
      if (!r.row && r.type !== 'batchReserve') {
        return
      }
      let req
      if (r.type === 'stageFail') {
        let failLabel = r.kind === 'demo' ? '试讲未通过' : '面试未通过'
        req = this.action('/tchRecruit/interview/fail-reject',
          {tchId: r.row.tchId, failKind: r.kind, reason: text},
          tchName(r.row) + ' 已标记为「' + failLabel + '」并转到已审核')
      } else if (r.type === 'offerReject') {
        req = this.action('/tchRecruit/interview/offer',
          {tchId: r.row.tchId, offerStatus: 'rejected', rejectReason: text},
          '发Offer状态已更新为「拒绝Offer」')
      } else if (r.type === 'reserve') {
        req = this.action('/tchRecruit/interview/to-reserve', {tchId: r.row.tchId, reason: text},
          tchName(r.row) + ' 已转储备')
      } else if (r.type === 'batchReserve') {
        this.reasonLoading = true
        this.runBatch('/tchRecruit/interview/batch-to-reserve', {tchIds: this.selectedTchIds(), reason: text}, 'reserve')
          .then((ok) => {
            this.reasonLoading = false
            if (ok) {
              this.reasonVisible = false
            }
          })
        return
      } else {
        return
      }
      this.reasonLoading = true
      req.then((ok) => {
        this.reasonLoading = false
        if (ok) {
          this.reasonVisible = false
        }
      })
    }
  }
}
</script>
<style lang="less" scoped>
.pending-interview-page { padding: 20px; }
.breadcrumb { margin-bottom: 16px; }
.page-head { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.page-title { margin: 0; font-size: 20px; font-weight: 700; color: #1c2438; }
.page-sub { margin-top: 4px; font-size: 13px; color: #808695; }
.page-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.pill { display: inline-block; padding: 3px 12px; border-radius: 14px; font-size: 12px; font-weight: 600; }
.pill.is-blue { border: 1px solid #b3d7ff; background: #f0f7ff; color: #2d8cf0; }
.pill.is-green { border: 1px solid #b7ebd5; background: #edfaf4; color: #19be6b; }
.search { margin-bottom: 16px; padding: 16px 16px 0; border-radius: 4px; background: #f8f8f9; }
.batch-bar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.batch-hint { margin-right: 4px; font-size: 13px; color: #808695; }
.referrer-field { display: flex; align-items: center; }
.referrer-field .ivu-input-wrapper { flex: 1; margin-right: 8px; }
.referrer-field .ivu-checkbox-wrapper { flex: none; margin-right: 0; white-space: nowrap; }
.page { margin-top: 12px; text-align: right; }
.pending-interview-page /deep/ .status-pill { display: inline-block; padding: 1px 9px; border: 1px solid transparent; border-radius: 10px; font-size: 12px; line-height: 18px; white-space: nowrap; }
.pending-interview-page /deep/ .status-pill.tone-gray { border-color: #dddee1; background: #f8f8f9; color: #657180; }
.pending-interview-page /deep/ .status-pill.tone-blue { border-color: #b3d7ff; background: #f0f7ff; color: #2d8cf0; }
.pending-interview-page /deep/ .status-pill.tone-primary { background: #2d8cf0; color: #fff; }
.pending-interview-page /deep/ .status-pill.tone-green { background: #19be6b; color: #fff; }
.pending-interview-page /deep/ .status-pill.tone-red { background: #ed3f14; color: #fff; }
.pending-interview-page /deep/ .status-pill.tone-yellow { border-color: #ffe0a3; background: #fff8e6; color: #b26a00; }
.pending-interview-page /deep/ .status-pill.tone-rose { border-color: #ffccc7; background: #fff1f0; color: #cf1322; }
.pending-interview-page /deep/ .status-pill.is-clickable { cursor: pointer; }
.pending-interview-page /deep/ .status-pill.is-disabled { color: #bbbec4; cursor: not-allowed; }
.pending-interview-page /deep/ .cell-time { font-size: 11px; color: #808695; }
.pending-interview-page /deep/ .cell-reason { max-width: 120px; overflow: hidden; font-size: 11px; white-space: nowrap; text-overflow: ellipsis; color: #ed3f14; }
.pending-interview-page /deep/ .cell-grade { margin-left: 4px; font-weight: 700; color: #19be6b; }
.pending-interview-page /deep/ .invite-cell { white-space: nowrap; }
</style>



// WEBPACK FOOTER //
// TchPendingInterview.vue?9ec04924